import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateScreen, updateScreenId } from './utils/state/screensSlice';
import { updateCanvasScreenId } from './utils/state/canvasSlice';
import { updateScreenIdInExperience } from './utils/state/experienceSlice';
import { ChevronDown, ChevronRight, Settings } from 'lucide-react';
import URLInputWithLibrary from './URLInputWithLibrary';
import FlowSelector from './FlowSelector';
import CompositeDesigner from './CompositeDesigner';
import { useReplicate } from './ReplicateContext';
import { ReplicatePropertyCheckbox } from './ReplicatePropertyCheckbox';

// Default values for all possible screen properties
const SCREEN_PROPERTY_DEFAULTS: { [key: string]: any } = {
    // Display properties
    'display.backgroundColor': '#ffffff',
    'display.backgroundMediaUrl': '',
    'display.foregroundColor': '#000000',
    'display.buttonColor': '#007AFF',
    'display.buttonTextColor': '#ffffff',
    'display.horizontalPadding': 20,
    'display.verticalPadding': 20,

    // Stage properties
    'stage.type': 'attract',
    'stage.mode': 'photo',
    'stage.flowId': '',
    'stage.shouldNavigateAfterCapture': true,
    'stage.postCaptureDestination': '',
    'stage.prompt': '',
    'stage.countDown': 3,
    'stage.processingMessage': 'Processing...',
    'stage.captureMessage': 'Smile!',
    'stage.dynamicTextIdentifier': '',
    'stage.captureOnMount': false,
    'stage.photoCount': 1,
    'stage.skipGifGeneration': false,
    'stage.gifDuration': undefined,
    'stage.gifCaptureDuration': undefined,
    'stage.includeIndividualPhotos': true,
    'stage.timeout': undefined,
    'stage.videoProcessingFlowId': undefined,
    'stage.postCaptureAction': undefined,
    'stage.valueKey': undefined,
    'stage.navigateAfterCaptureDelay': 0,
    'stage.hasNoTimeout': false,
    'stage.takeFirstPhotoImmediately': false,

    // Processing properties
    'stage.processingType': 'composite',
    'stage.processingFlowId': '',
    'stage.inputs': [],
    'stage.expectedPhotoCount': 0,
    'stage.processingDynamicTextIdentifier': '',
    'stage.processingHeight': 1800,
    'stage.processingWidth': 600,
    'stage.overlayUrl': '',
    'stage.backgroundUrl': '',
    'stage.shouldNavigateAfterProcessing': true,
    'stage.postProcessingDestination': '',
    'stage.processingPhotoInstructions': [],
    'stage.autoPrint': false,

    // Sharing properties
    'sharing.isEnabled': false,
    'sharing.buttonText': 'Share',
    'sharing.buttonColor': '#007AFF',
    'sharing.buttonTextColor': '#ffffff',
    'sharing.methods': [],
    'sharing.emailTemplate': '',
    'sharing.smsTemplate': '',
    'sharing.message': '',
    'sharing.hashtags': [],
    'sharing.url': '',
    'sharing.title': '',
    'sharing.description': '',

    // Metadata properties
    'metadata.title': '',
    'metadata.description': '',
    'metadata.tags': [],
    'metadata.category': '',
    'metadata.version': '1.0.0',
    'metadata.lastModified': new Date().toISOString(),
    'metadata.author': '',
    'metadata.notes': '',
};

interface ComprehensiveScreenEditorProps {
    screen: ScreenDefinition;
    onIdChange: (newId: string) => void;
}

interface PropertySection {
    title: string;
    icon: string;
    properties: string[];
}

const SCREEN_PROPERTY_SECTIONS: PropertySection[] = [
    {
        title: 'Display Properties',
        icon: '🎨',
        properties: [
            'display.backgroundColor',
            'display.backgroundMediaUrl',
            'display.foregroundColor',
            'display.buttonColor',
            'display.buttonTextColor',
            'display.horizontalPadding',
            'display.verticalPadding',
        ],
    },
    {
        title: 'Stage Configuration',
        icon: '🎭',
        properties: ['stage.type'],
    },
    {
        title: 'Capture Settings',
        icon: '📸',
        properties: [
            'stage.mode',
            'stage.flowId',
            'stage.shouldNavigateAfterCapture',
            'stage.postCaptureDestination',
            'stage.prompt',
            'stage.countDown',
            'stage.processingMessage',
            'stage.captureMessage',
            'stage.dynamicTextIdentifier',
            'stage.captureOnMount',
            'stage.photoCount',
            'stage.skipGifGeneration',
            'stage.gifDuration',
            'stage.gifCaptureDuration',
            'stage.includeIndividualPhotos',
            'stage.timeout',
            'stage.videoProcessingFlowId',
            'stage.postCaptureAction',
            'stage.valueKey',
            'stage.navigateAfterCaptureDelay',
            'stage.hasNoTimeout',
            'stage.takeFirstPhotoImmediately',
        ],
    },
    {
        title: 'Processing Settings',
        icon: '⚙️',
        properties: [
            'stage.processingType',
            'stage.processingFlowId',
            'stage.inputs',
            'stage.expectedPhotoCount',
            'stage.processingMessage',
            'stage.processingDynamicTextIdentifier',
            'stage.processingHeight',
            'stage.processingWidth',
            'stage.overlayUrl',
            'stage.backgroundUrl',
            'stage.shouldNavigateAfterProcessing',
            'stage.postProcessingDestination',
            'stage.processingPhotoInstructions',
            'stage.autoPrint',
        ],
    },
    {
        title: 'Navigation',
        icon: '🔄',
        properties: [
            'navigateDestination',
            'navigateAfterTimeout',
            'resetAfterTimeout',
        ],
    },
    {
        title: 'Sharing',
        icon: '📤',
        properties: [
            'sharing.isEnabled',
            'sharing.buttonText',
            'sharing.buttonColor',
            'sharing.buttonTextColor',
            'sharing.methods',
            'sharing.emailTemplate',
            'sharing.smsTemplate',
            'sharing.message',
            'sharing.hashtags',
            'sharing.url',
            'sharing.title',
            'sharing.description',
        ],
    },
    {
        title: 'Metadata',
        icon: '📝',
        properties: [
            'metadata.title',
            'metadata.description',
            'metadata.tags',
            'metadata.category',
            'metadata.version',
            'metadata.author',
            'metadata.notes',
        ],
    },
];

export default function ComprehensiveScreenEditor({
    screen,
    onIdChange,
}: ComprehensiveScreenEditorProps) {
    const {
        replicateMode,
        setSourceScreenId,
        setSourceElementId,
        setShowReplicateDialog,
        selectedProperties,
    } = useReplicate();
    const dispatch = useDispatch();
    const { activationId } = useParams();
    const [isCompositeDesignerOpen, setIsCompositeDesignerOpen] =
        useState(false);
    const screens = useSelector((state: any) => state.screens.present.entities);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['Display Properties', 'Stage Configuration']),
    );

    // Local state for screen ID to prevent immediate updates
    const [localScreenId, setLocalScreenId] = useState(screen.id);
    const [idUpdateTimeout, setIdUpdateTimeout] =
        useState<NodeJS.Timeout | null>(null);

    // Update local state when screen prop changes (e.g., when switching screens)
    useEffect(() => {
        setLocalScreenId(screen.id);
    }, [screen.id]);

    // Debounced screen ID update function
    const debouncedUpdateScreenId = useCallback(
        (newId: string) => {
            if (idUpdateTimeout) {
                clearTimeout(idUpdateTimeout);
            }

            // Capture the current screen ID when the debounce is triggered
            const currentScreenId = screen.id;
            const trimmedNewId = newId.trim();

            // Skip if the new ID is empty or the same as current
            if (trimmedNewId === '' || trimmedNewId === currentScreenId) {
                return;
            }

            const timeout = setTimeout(() => {
                // Update all states together to avoid timing issues
                dispatch(
                    updateScreenId({
                        id: currentScreenId,
                        newId: trimmedNewId,
                    }),
                );
                dispatch(
                    updateCanvasScreenId({
                        oldId: currentScreenId,
                        newId: trimmedNewId,
                    }),
                );

                // Update the experience's screen list if we have an activation ID
                if (activationId) {
                    dispatch(
                        updateScreenIdInExperience({
                            experienceId: +activationId,
                            oldId: currentScreenId,
                            newId: trimmedNewId,
                        }),
                    );
                }

                // Notify parent in next tick to ensure Redux updates are processed
                setTimeout(() => {
                    onIdChange(trimmedNewId);
                }, 0);
            }, 500); // 500ms delay

            setIdUpdateTimeout(timeout);
        },
        [dispatch, screen.id, onIdChange, idUpdateTimeout, activationId],
    );

    // Handle screen ID input changes
    const handleScreenIdChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newId = e.target.value;
            setLocalScreenId(newId);
            debouncedUpdateScreenId(newId);
        },
        [debouncedUpdateScreenId],
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (idUpdateTimeout) {
                clearTimeout(idUpdateTimeout);
            }
        };
    }, [idUpdateTimeout]);

    const toggleSection = (sectionTitle: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionTitle)) {
            newExpanded.delete(sectionTitle);
        } else {
            newExpanded.add(sectionTitle);
        }
        setExpandedSections(newExpanded);
    };

    const updateScreenProperty = (propertyPath: string, value: any) => {
        if (propertyPath.startsWith('display.')) {
            const displayProp = propertyPath.replace('display.', '');
            dispatch(
                updateScreen({
                    id: screen.id,
                    data: {
                        displayProperties: {
                            ...screen.displayProperties,
                            [displayProp]: value,
                        },
                    },
                }),
            );
        } else if (propertyPath.startsWith('stage.')) {
            const stageProp = propertyPath.replace('stage.', '');
            if (stageProp === 'type') {
                dispatch(
                    updateScreen({
                        id: screen.id,
                        data: {
                            stage: {
                                ...screen.stage,
                                type: value,
                            },
                        },
                    }),
                );
            } else {
                dispatch(
                    updateScreen({
                        id: screen.id,
                        data: {
                            stage: {
                                ...screen.stage,
                                metadata: {
                                    ...screen.stage.metadata,
                                    [stageProp]: value,
                                },
                            },
                        },
                    }),
                );
            }
        } else {
            dispatch(
                updateScreen({
                    id: screen.id,
                    data: {
                        [propertyPath]: value,
                    },
                }),
            );
        }
    };

    const getPropertyValue = (propertyPath: string): any => {
        let value: any;

        if (propertyPath.startsWith('display.')) {
            const displayProp = propertyPath.replace('display.', '');
            value = screen.displayProperties
                ? (screen.displayProperties as any)[displayProp]
                : undefined;
        } else if (propertyPath.startsWith('stage.')) {
            const stageProp = propertyPath.replace('stage.', '');
            if (stageProp === 'type') {
                value = screen.stage?.type;
            } else {
                value = screen.stage?.metadata
                    ? (screen.stage.metadata as any)[stageProp]
                    : undefined;
            }
        } else if (propertyPath.startsWith('sharing.')) {
            const sharingProp = propertyPath.replace('sharing.', '');
            const sharing = (screen as any).sharing;
            value = sharing ? sharing[sharingProp] : undefined;
        } else if (propertyPath.startsWith('metadata.')) {
            const metadataProp = propertyPath.replace('metadata.', '');
            value = screen.metadata
                ? (screen.metadata as any)[metadataProp]
                : undefined;
        } else {
            value = (screen as any)[propertyPath];
        }

        // Return default value if property is missing or undefined
        if (value === undefined || value === null) {
            return SCREEN_PROPERTY_DEFAULTS[propertyPath];
        }

        return value;
    };

    // Helper function to render property with replicate checkbox
    const renderPropertyWithReplicate = (
        propertyName: string,
        label: string,
        inputElement: React.ReactNode,
    ) => (
        <div key={propertyName} className="mb-3">
            <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700">
                    {label}
                </label>
                {replicateMode && (
                    <ReplicatePropertyCheckbox
                        property={propertyName}
                        label={label}
                    />
                )}
            </div>
            {inputElement}
        </div>
    );

    const renderPropertyField = (propertyName: string) => {
        const value = getPropertyValue(propertyName);

        const handleChange = (newValue: any) => {
            updateScreenProperty(propertyName, newValue);
        };

        // Skip properties that don't apply to this stage type
        if (!isPropertyApplicable(propertyName, screen.stage.type)) {
            return null;
        }

        switch (propertyName) {
            case 'stage.type':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Stage Type',
                    <select
                        value={value || 'attract'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="attract">Attract</option>
                        <option value="capture">Capture</option>
                        <option value="processing">Processing</option>
                        <option value="review">Review</option>
                        <option value="share">Share</option>
                        <option value="query">Query</option>
                    </select>,
                );

            case 'stage.mode':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Capture Mode
                        </label>
                        <select
                            value={value || 'photo'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="photo">Photo</option>
                            <option value="gif">GIF</option>
                            <option value="clicker">Clicker</option>
                            <option value="sequence">Sequence</option>
                        </select>
                    </div>
                );

            case 'stage.processingType':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Processing Type
                        </label>
                        <select
                            value={value || 'composite'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="composite">Composite</option>
                        </select>
                    </div>
                );

            case 'stage.processingPhotoInstructions':
                const processingType = getPropertyValue('stage.processingType');
                if (processingType === 'composite') {
                    return (
                        <div key={propertyName} className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Photo Layout (
                                {Array.isArray(value) ? value.length : 0}{' '}
                                photos)
                            </label>
                            <button
                                onClick={() => setIsCompositeDesignerOpen(true)}
                                className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <Settings className="w-4 h-4" />
                                <span>Open Composite Designer</span>
                            </button>
                        </div>
                    );
                }
                return null;

            // Boolean inputs
            case 'stage.shouldNavigateAfterCapture':
            case 'stage.shouldNavigateAfterProcessing':
            case 'stage.captureOnMount':
            case 'stage.skipGifGeneration':
            case 'stage.includeIndividualPhotos':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={value || false}
                                onChange={(e) => handleChange(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                {formatPropertyName(propertyName)}
                            </span>
                        </label>
                    </div>
                );

            // Flow selectors
            case 'stage.flowId':
            case 'stage.processingFlowId':
            case 'stage.videoProcessingFlowId':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <FlowSelector
                            value={value || undefined}
                            onChange={(newValue) =>
                                handleChange(
                                    Array.isArray(newValue)
                                        ? newValue[0]
                                        : newValue,
                                )
                            }
                            multiple={false}
                            placeholder="Select a processing flow..."
                        />
                    </div>
                );

            // Number inputs
            case 'stage.countDown':
            case 'stage.photoCount':
            case 'stage.expectedPhotoCount':
            case 'stage.processingHeight':
            case 'stage.processingWidth':
            case 'stage.gifDuration':
            case 'stage.gifCaptureDuration':
            case 'navigateAfterTimeout':
            case 'resetAfterTimeout':
            case 'display.horizontalPadding':
            case 'display.verticalPadding':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <input
                            type="number"
                            value={value || 0}
                            onChange={(e) =>
                                handleChange(parseInt(e.target.value) || 0)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={0}
                        />
                    </div>
                );

            // Color inputs
            case 'display.backgroundColor':
            case 'display.foregroundColor':
            case 'display.buttonColor':
            case 'display.buttonTextColor':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <div className="flex gap-2">
                        <input
                            type="color"
                            value={value || '#000000'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="#000000"
                        />
                    </div>,
                );

            // URL inputs with library shortcut
            case 'display.backgroundMediaUrl':
            case 'stage.overlayUrl':
            case 'stage.backgroundUrl':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <URLInputWithLibrary
                        value={value}
                        onChange={handleChange}
                        placeholder={getPropertyPlaceholder(propertyName)}
                        label={formatPropertyName(propertyName)}
                    />,
                );

            case 'stage.postCaptureDestination':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Post Capture Destination
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) =>
                                handleChange(e.target.value || undefined)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                Select destination screen...
                            </option>
                            {Object.values(screens)
                                .filter(
                                    (screenOption: any) =>
                                        screenOption.id !== screen.id,
                                ) // Don't show current screen
                                .map((screenOption: any) => (
                                    <option
                                        key={screenOption.id}
                                        value={screenOption.id}
                                    >
                                        {screenOption.id}
                                    </option>
                                ))}
                        </select>
                    </div>
                );

            case 'stage.postProcessingDestination':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Post Processing Destination
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) =>
                                handleChange(e.target.value || undefined)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                Select destination screen...
                            </option>
                            {Object.values(screens)
                                .filter(
                                    (screenOption: any) =>
                                        screenOption.id !== screen.id,
                                ) // Don't show current screen
                                .map((screenOption: any) => (
                                    <option
                                        key={screenOption.id}
                                        value={screenOption.id}
                                    >
                                        {screenOption.id}
                                    </option>
                                ))}
                        </select>
                    </div>
                );

            case 'navigateDestination':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Navigate Destination
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) =>
                                handleChange(e.target.value || undefined)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                Select destination screen...
                            </option>
                            {Object.values(screens)
                                .filter(
                                    (screenOption: any) =>
                                        screenOption.id !== screen.id,
                                ) // Don't show current screen
                                .map((screenOption: any) => (
                                    <option
                                        key={screenOption.id}
                                        value={screenOption.id}
                                    >
                                        {screenOption.id}
                                    </option>
                                ))}
                        </select>
                    </div>
                );

            // Text inputs
            case 'stage.prompt':
            case 'stage.processingMessage':
            case 'stage.captureMessage':
            case 'stage.dynamicTextIdentifier':
            case 'stage.processingDynamicTextIdentifier':
            case 'stage.inputs':
            default:
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={getPropertyPlaceholder(propertyName)}
                        />
                    </div>
                );
        }
    };

    const isSectionApplicable = (
        sectionTitle: string,
        stageType: string,
    ): boolean => {
        switch (sectionTitle) {
            case 'Capture Settings':
                return stageType === 'capture';
            case 'Processing Settings':
                return stageType === 'processing';
            default:
                return true; // All other sections are always shown
        }
    };

    const isPropertyApplicable = (
        propertyName: string,
        stageType: string,
    ): boolean => {
        // Display properties are always applicable
        if (propertyName.startsWith('display.')) {
            return true;
        }

        // Stage type is always applicable
        if (propertyName === 'stage.type') {
            return true;
        }

        // Metadata properties are always applicable
        if (propertyName.startsWith('metadata.')) {
            return true;
        }

        // Navigation properties are always applicable
        if (
            [
                'navigateDestination',
                'navigateAfterTimeout',
                'resetAfterTimeout',
            ].includes(propertyName)
        ) {
            return true;
        }

        // Sharing properties are always applicable
        if (propertyName.startsWith('sharing.')) {
            return true;
        }

        // Capture-specific properties only show for capture screens
        const captureProperties = [
            'stage.mode',
            'stage.flowId',
            'stage.shouldNavigateAfterCapture',
            'stage.postCaptureDestination',
            'stage.prompt',
            'stage.countDown',
            'stage.processingMessage',
            'stage.captureMessage',
            'stage.dynamicTextIdentifier',
            'stage.captureOnMount',
            'stage.photoCount',
            'stage.skipGifGeneration',
            'stage.gifDuration',
            'stage.gifCaptureDuration',
            'stage.includeIndividualPhotos',
            'stage.timeout',
            'stage.videoProcessingFlowId',
            'stage.postCaptureAction',
            'stage.valueKey',
            'stage.navigateAfterCaptureDelay',
            'stage.hasNoTimeout',
            'stage.takeFirstPhotoImmediately',
        ];
        if (captureProperties.includes(propertyName)) {
            return stageType === 'capture';
        }

        // Processing-specific properties only show for processing screens
        const processingProperties = [
            'stage.processingType',
            'stage.processingFlowId',
            'stage.inputs',
            'stage.expectedPhotoCount',
            'stage.processingDynamicTextIdentifier',
            'stage.processingHeight',
            'stage.processingWidth',
            'stage.overlayUrl',
            'stage.backgroundUrl',
            'stage.shouldNavigateAfterProcessing',
            'stage.postProcessingDestination',
            'stage.processingPhotoInstructions',
            'stage.autoPrint',
        ];
        if (processingProperties.includes(propertyName)) {
            return stageType === 'processing';
        }

        // Show all other properties by default
        return true;
    };

    const formatPropertyName = (propertyName: string): string => {
        // Remove the prefix (display., stage.) for display
        const cleanName = propertyName.replace(/^(display|stage)\./, '');
        return cleanName
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const getPropertyPlaceholder = (propertyName: string): string => {
        const placeholders: { [key: string]: string } = {
            backgroundMediaUrl: 'https://example.com/background.jpg',
            postCaptureDestination: 'screen-id',
            postProcessingDestination: 'screen-id',
            navigateDestination: 'screen-id',
            prompt: 'Get ready for your photo!',
            processingMessage: 'Processing your photo...',
            captureMessage: 'Smile!',
            dynamicTextIdentifier: 'capture-message',
            processingDynamicTextIdentifier: 'processing-message',
            overlayUrl: 'https://example.com/overlay.png',
            backgroundUrl: 'https://example.com/background.jpg',
            inputs: 'screen-id-1,screen-id-2',
        };
        return placeholders[propertyName] || '';
    };

    return (
        <div className="space-y-4">
            {/* Screen ID Field */}
            <div className="p-3 bg-gray-50 rounded-lg border">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Screen ID
                </label>
                <input
                    type="text"
                    value={localScreenId}
                    onChange={handleScreenIdChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Screen Stats */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                        <div className="text-lg font-semibold text-green-700">
                            {screen.elements.length}
                        </div>
                        <div className="text-xs text-green-600">Elements</div>
                    </div>
                    <div>
                        <div className="text-lg font-semibold text-green-700">
                            {screen.stage.type}
                        </div>
                        <div className="text-xs text-green-600">Stage</div>
                    </div>
                </div>
            </div>

            {/* Replicate Mode Controls */}
            {replicateMode && (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-sm text-gray-700">
                        Replicate screen properties to other screens
                    </div>

                    <button
                        onClick={() => {
                            setSourceScreenId(screen.id);
                            setSourceElementId(null); // Clear element since we're replicating screen
                            setShowReplicateDialog(true);
                        }}
                        disabled={selectedProperties.length === 0}
                        className="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Replicate ({selectedProperties.length})
                    </button>
                </div>
            )}
            {/* Property Sections */}
            {SCREEN_PROPERTY_SECTIONS.map((section) => {
                // Check if the entire section should be shown for this stage type
                if (!isSectionApplicable(section.title, screen.stage.type)) {
                    return null;
                }

                const applicableProperties = section.properties.filter((prop) =>
                    isPropertyApplicable(prop, screen.stage.type),
                );

                if (applicableProperties.length === 0) return null;

                const isExpanded = expandedSections.has(section.title);

                return (
                    <div
                        key={section.title}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                        <button
                            onClick={() => toggleSection(section.title)}
                            className="w-full max-w-full p-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors overflow-hidden"
                        >
                            <div className="flex items-center gap-2 flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                                <span className="text-sm flex-shrink-0">
                                    {section.icon}
                                </span>
                                <span className="text-sm font-medium text-gray-900 truncate">
                                    {section.title}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                    ({applicableProperties.length})
                                </span>
                            </div>
                            <div className="flex-shrink-0 w-8 h-6 flex items-center justify-center pr-2">
                                {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                )}
                            </div>
                        </button>

                        {isExpanded && (
                            <div className="p-3 border-t border-gray-200 bg-gray-50">
                                {applicableProperties.map((propertyName) =>
                                    renderPropertyField(propertyName),
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Composite Designer */}
            {isCompositeDesignerOpen && (
                <CompositeDesigner
                    isOpen={isCompositeDesignerOpen}
                    onClose={() => setIsCompositeDesignerOpen(false)}
                    width={getPropertyValue('stage.processingWidth') || 600}
                    height={getPropertyValue('stage.processingHeight') || 1800}
                    backgroundUrl={
                        getPropertyValue('stage.backgroundUrl') || ''
                    }
                    overlayUrl={getPropertyValue('stage.overlayUrl') || ''}
                    photoInstructions={
                        getPropertyValue('stage.processingPhotoInstructions') ||
                        []
                    }
                    expectedPhotoCount={
                        getPropertyValue('stage.expectedPhotoCount') || 0
                    }
                    onSave={(
                        instructions,
                        expectedCount,
                        backgroundUrl,
                        overlayUrl,
                    ) => {
                        // Create a deep copy of the screen to avoid mutation
                        const updatedScreen = JSON.parse(
                            JSON.stringify(screen),
                        );

                        // Ensure stage and metadata exist
                        if (!updatedScreen.stage) {
                            updatedScreen.stage = {
                                type: 'processing',
                                metadata: {},
                            };
                        }
                        if (!updatedScreen.stage.metadata) {
                            updatedScreen.stage.metadata = {};
                        }

                        // Update the processing photo instructions and related properties
                        updatedScreen.stage.metadata.processingPhotoInstructions =
                            instructions;
                        updatedScreen.stage.metadata.expectedPhotoCount =
                            expectedCount;

                        if (backgroundUrl !== undefined) {
                            updatedScreen.stage.metadata.backgroundUrl =
                                backgroundUrl;
                        }
                        if (overlayUrl !== undefined) {
                            updatedScreen.stage.metadata.overlayUrl =
                                overlayUrl;
                        }

                        // Use the proper Redux action with the screen object
                        dispatch(
                            updateScreen({
                                id: screen.id,
                                screen: updatedScreen,
                            }),
                        );
                    }}
                />
            )}
        </div>
    );
}
